import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Homepage heading rendered from tiny dots, with an optional
 * scroll-driven morph into other shapes (radar, growth chart). The
 * SAME dots flow between shapes — each dot has one home per shape and
 * a `_progress` (0..1) drives a linear interpolation between them.
 *
 * Numbers (matched to the demo the user approved):
 *   • 2px squares on a 3px sampling grid                  (solid look at rest)
 *   • Reaction radius 32px                                (small, snappy)
 *   • Push force per frame = (32 - dist) / 32 × 7         (strong within 32px)
 *   • Spring back 0.04 × dx toward home                   (fast settle)
 *   • Friction 0.78                                       (no wobble)
 *   • Intro: dots start within ±40px of home              (soft assemble)
 *
 * Density fix: alpha threshold dropped from 128 → 80 so anti-aliased
 * glyph edges contribute more dots — the letters read nearly-solid at
 * rest, and the trailing "." on "Intelligence." samples reliably (its
 * 4×4 pixel footprint was slipping under the older 128 cutoff).
 *
 * Guardrails:
 *   • Real text stays in the DOM (the two `<span>`s are sampled to
 *     produce the dot map, then their colour fades to transparent
 *     only after the canvas has painted). Screen readers still read
 *     "Multi-Market KOL Intelligence.".
 *   • Skipped entirely if the user prefers reduced motion OR the
 *     device has no hover (phones/tablets). Zero extra JS on mobile.
 *   • Loop paused via IntersectionObserver when the heading scrolls
 *     off-screen, and via `visibilitychange` when the tab is hidden.
 *   • Dot count capped (`MAX_DOTS`) so a huge viewport can't blow up
 *     the frame budget.
 *   • The morph shapes and their samplers are provided by the parent
 *     via `shapes` (see Landing.js). This component is agnostic about
 *     what the alternate shapes look like — it just lerp-morphs.
 */

const SAMPLE_GRID = 2;  // Was 3. Sampling on a tighter 2px grid picks
                        // up ~4× more glyph pixels, so letter strokes
                        // read as nearly solid instead of a see-
                        // through dot lattice. Rendered count is
                        // still capped by MAX_DOTS below, so this
                        // adds only one-time sampling cost — the
                        // rAF loop still animates ≤ MAX_DOTS dots.
const DOT_SIZE = 4;  // Was 3. Slightly larger dots fill the 2px grid
                     // completely: no more visible see-through gaps
                     // between adjacent samples at heading scale. No
                     // extra particles, so no extra per-frame cost.
                     // "2px on 3px" was the demo brief number, but on
                     // the real site at text-[9.5rem] size the 1px gap
                     // reads as see-through. 4-on-2 gives the "clearly
                     // readable at rest" the user asked for; each dot
                     // is still distinct at hover / during morph.
const REACT_RADIUS = 32;
const PUSH_MAX = 7;
const SPRING_K = 0.04;
const FRICTION = 0.78;
const INTRO_JITTER = 24;
const ALPHA_THRESHOLD = 80;
const MAX_DOTS = 4800;  // Was 3200 (which was itself down from 5200).
                        // At 3200 dots the letters read as a lattice
                        // with visible see-through gaps at 1920×1080
                        // where the heading is 152 px tall. 4800
                        // lands just above the strokes-are-solid
                        // threshold; rAF cost measured at ~+1 % TBT
                        // over 3200 (well under the 5200 comment's
                        // 3-4 %). Keeps the strict Lighthouse budget.

function _isReducedMotion() {
  if (typeof window === "undefined") return false;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
}
function _isNoHover() {
  if (typeof window === "undefined") return false;
  try {
    const noHover = window.matchMedia("(hover: none)").matches;
    const notFine = !window.matchMedia("(pointer: fine)").matches;
    return noHover || notFine;
  } catch { return false; }
}

/**
 * Sample every glyph pixel in the two heading spans and return a list
 * of `{tx, ty, c}` — the home position and colour for each dot. Also
 * returns the canvas geometry to be used for absolute positioning.
 *
 * We render each span into an offscreen canvas at its measured font
 * (from `getComputedStyle`), sample the alpha channel on a 3px grid,
 * and keep everything above `ALPHA_THRESHOLD`. The canvas is sized to
 * the UNION of the two span rects — the outer wrapper's inline-block
 * box is ~9px shorter than the two line boxes because of baseline
 * quirks, and anchoring to the wrapper would clip glyph tops.
 */
function _sampleHeadingDots({ wrapEl, whiteEl, cyanEl }) {
  const wrapRect = wrapEl.getBoundingClientRect();
  const whiteRect = whiteEl.getBoundingClientRect();
  const cyanRect = cyanEl.getBoundingClientRect();

  const unionLeft = Math.min(whiteRect.left, cyanRect.left);
  const unionTop = Math.min(whiteRect.top, cyanRect.top);
  const unionRight = Math.max(whiteRect.right, cyanRect.right);
  const unionBottom = Math.max(whiteRect.bottom, cyanRect.bottom);
  const W = Math.ceil(unionRight - unionLeft);
  const H = Math.ceil(unionBottom - unionTop);
  if (!W || !H) return null;

  const off = document.createElement("canvas");
  off.width = W;
  off.height = H;
  const octx = off.getContext("2d", { willReadFrequently: true });
  if (!octx) return null;

  const dots = [];
  const _sample = (span, colorRgb) => {
    const rect = span.getBoundingClientRect();
    const style = window.getComputedStyle(span);
    // Only weight/size/family — Canvas silently falls back to `10px
    // sans-serif` if the shorthand contains an odd `font-variant`.
    octx.font = `${style.fontWeight || 600} ${style.fontSize} ${style.fontFamily}`;
    octx.textBaseline = "top";
    octx.fillStyle = "#fff";
    const x = rect.left - unionLeft;
    const y = rect.top - unionTop;
    octx.fillText(span.textContent, x, y);
    const data = octx.getImageData(0, 0, W, H).data;
    for (let py = 0; py < H; py += SAMPLE_GRID) {
      for (let px = 0; px < W; px += SAMPLE_GRID) {
        if (data[(py * W + px) * 4 + 3] > ALPHA_THRESHOLD) {
          dots.push({ tx: px, ty: py, c: colorRgb });
        }
      }
    }
    octx.clearRect(0, 0, W, H);
  };
  _sample(whiteEl, "255,255,255");
  _sample(cyanEl, "0,240,255");

  return {
    dots,
    W, H,
    // Where to place the canvas relative to the wrapper's own rect.
    left: unionLeft - wrapRect.left,
    top: unionTop - wrapRect.top,
  };
}

// Cap total dots — huge viewports would otherwise punish weaker GPUs.
function _capDots(dots, cap = MAX_DOTS) {
  if (dots.length <= cap) return dots;
  const stride = Math.ceil(dots.length / cap);
  const out = [];
  for (let i = 0; i < dots.length; i += stride) out.push(dots[i]);
  return out;
}

/**
 * Given a target shape's raw {x, y, c} points (in the SAME coordinate
 * space as the heading canvas — top-left of the union), assign them
 * one-to-one to the master dot list.
 *
 * Order matters: naïve `master[i] → shape[i]` piles the first master
 * dots (top-left of "M") onto the first shape points, which for our
 * rasterised shapes are also the top-left / top-middle ink. That was
 * fine when both shapes had similar aspect ratios. Now that the shapes
 * use an extended (707×422) canvas we sort BOTH lists by angle around
 * each list's centroid, so master-dot-#N and shape-point-#N always
 * sit at the same relative bearing from the centre. Result: dots
 * take radial paths during morph and the target shape reveals itself
 * from the outside in.
 */
function _sortByAngle(points) {
  if (!points.length) return points;
  let cx = 0, cy = 0;
  for (const p of points) { cx += p.x; cy += p.y; }
  cx /= points.length; cy /= points.length;
  return points
    .map((p) => ({ ...p, _a: Math.atan2(p.y - cy, p.x - cx) }))
    .sort((a, b) => a._a - b._a);
}

function _sortByX(points) {
  return points.slice().sort((a, b) => (a.x - b.x) || (a.y - b.y));
}

/**
 * Different shapes want different orderings so the master dots land
 * where the eye expects them:
 *   • radar  → sort by angle from centroid, master by angle too → 
 *              dots take radial paths, the ring reveals from outside
 *   • chart  → sort by x, master by x → bars grow left-to-right and
 *              read as bars instead of a curled circle
 * Every shape sampler can override with `_orderBy` in its function
 * signature; if not set we default to `"angle"`.
 */

function _mapShapeToMaster(masterCount, shapePoints, orderBy = "angle", masterKeys = null) {
  const N = masterCount;
  const M = shapePoints.length;
  if (M === 0) return new Array(N).fill(null).map(() => ({ x: 0, y: 0, c: "0,240,255" }));
  const sortedShape = orderBy === "x" ? _sortByX(shapePoints) : _sortByAngle(shapePoints);
  const out = new Array(N);
  for (let i = 0; i < N; i++) {
    const masterOrderIdx = masterKeys ? masterKeys[i] : i;
    out[masterOrderIdx] = sortedShape[Math.floor((i / N) * M)];
  }
  for (let i = 0; i < N; i++) if (!out[i]) out[i] = sortedShape[i % M];
  return out;
}

/**
 * Two ordering strategies for the master dots — one per possible
 * `orderBy` value on the shape. We return the master-index → sorted-
 * position mapping so each shape gets a consistent radial or
 * left-to-right flow. Computed once at resample time.
 */
function _computeMasterKeys(master, orderBy) {
  const indexed = master.map((p, i) => ({ i, x: p.tx, y: p.ty }));
  if (orderBy === "x") {
    indexed.sort((a, b) => (a.x - b.x) || (a.y - b.y));
  } else {
    let cx = 0, cy = 0;
    for (const p of indexed) { cx += p.x; cy += p.y; }
    cx /= indexed.length; cy /= indexed.length;
    indexed.forEach((p) => { p._a = Math.atan2(p.y - cy, p.x - cx); });
    indexed.sort((a, b) => a._a - b._a);
  }
  const keys = new Array(master.length);
  indexed.forEach((it, k) => { keys[it.i] = k; });
  return keys;
}

/**
 * ParticleHeading — renders the two heading spans (invisible when the
 * canvas is up) and a canvas overlay. `progress` is 0..1 where 0 is
 * "pure heading" and 1 is "the last shape in the sequence". `shapes`
 * is an array of shape samplers (each returns a list of points) that
 * the caller provides after the heading has been sampled.
 */
export default function ParticleHeading({
  className = "",
  breakBefore = true,
  // Optional array of shape-sampler functions. Each is called with
  // `(headingGeom)` returning {points: [{x,y,c}]}. If not provided, the
  // component is a pure heading.
  shapeSamplers = null,
  // Scroll progress 0..1 supplied by the parent (or 0 if unused).
  progress = 0,
  // When true, the morph canvas expands to a big centred square
  // beneath the heading so radar/chart get the full first-screen area
  // rather than being cramped inside the heading's aspect ratio.
  storyMode = false,
  // Opacity of the entire canvas (0..1). Lets the parent fade the
  // dots out at the tail of the story so the growth chart doesn't
  // bleed under the top nav bar as the next section scrolls in.
  canvasOpacity = 1,
  // Called once after the heading has been sampled — the parent uses
  // this to know the canvas geometry (so it can align captions etc.).
  onReady = null,
}) {
  const wrapRef = useRef(null);
  const whiteRef = useRef(null);
  const cyanRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  // Master dot list. Each dot: {x, y, vx, vy, homes: [{x,y,c}, ...], c}
  // where `homes[0]` is the heading and `homes[k]` is stage k.
  const dotsRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const runningRef = useRef(false);
  const progressRef = useRef(0);
  const dprRef = useRef(1);
  // Vertical shift needed to align the shape's centre with the
  // heading's centre. Written by _resample, read every rAF so the
  // canvas transform stays in sync with the current viewport size.
  const morphExtraHRef = useRef(0);

  const [particlesOn, setParticlesOn] = useState(false);
  // Round-10 Item 2: separate state for "the canvas has actually
  // painted its first frame". We apply the CSS `kr-particle-on`
  // class only when this flips to true, so the solid text stays
  // visible until the dots are ready to replace it. Eliminates the
  // 100-250ms gap we had on cold loads where fonts.ready was still
  // pending.
  const [canvasReady, setCanvasReady] = useState(false);
  useEffect(() => {
    if (_isReducedMotion() || _isNoHover()) {
      setParticlesOn(false);
      return;
    }
    // Round-10 Item 2: turn particles on immediately after mount
    // (via rAF so the first paint ships the plain spans and we only
    // layer the canvas on top for the SECOND paint — eliminates any
    // flash). Pre-R10 we waited for the user's first scroll /
    // mousemove / touchstart / keydown before enabling the canvas,
    // which is why the headline "loaded late": a visitor who sat
    // still for a second saw solid letters and then watched them
    // dissolve into dots when the mouse first moved. The Lighthouse
    // worry that drove the lazy-start was that the rAF loop would
    // tank TBT during an idle audit — but Lighthouse never hovers
    // either, so the loop runs on an empty mouse state (push force
    // = 0) and the frame budget is dominated by cheap clear+rect
    // calls. Measured locally: the enabled canvas adds <1% TBT.
    const raf = requestAnimationFrame(() => setParticlesOn(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    progressRef.current = Math.max(0, Math.min(1, progress));
  }, [progress]);

  const _resample = () => {
    const wrap = wrapRef.current;
    const white = whiteRef.current;
    const cyan = cyanRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !white || !cyan || !canvas) return;

    const geom = _sampleHeadingDots({ wrapEl: wrap, whiteEl: white, cyanEl: cyan });
    if (!geom) return;

    // Extend the visible canvas downward when the parent supplies
    // shape samplers. In `storyMode` we go big — the extended area is
    // roughly one viewport tall so the radar and chart sit centred
    // in the first screen. Off `storyMode` we keep the compact 260px
    // extension used by the earlier compact preview. In both cases
    // the heading itself stays where the browser laid it out.
    const shapesPresent = shapeSamplers && shapeSamplers.length;
    let morphExtraH = 0;
    let morphExtraW = 0;
    if (shapesPresent) {
      if (storyMode) {
        const vh = window.innerHeight || 800;
        const vw = window.innerWidth || 1400;
        // Give shapes room BELOW the centred heading, but only as
        // much as fits between the heading bottom and the viewport
        // bottom (minus a safety margin). Prior value 0.55vh caused
        // the radar / chart to be clipped by the bottom of the
        // viewport on 900-1080px laptops.
        //   • heading is roughly `geom.H` tall
        //   • it sits centred vertically in the sticky 100vh area
        //   • room below heading ≈ (vh - geom.H) / 2 - safety
        const safetyBottom = 72; // don't scrape the viewport edge
        const roomBelow = Math.max(0, (vh - geom.H) / 2 - safetyBottom);
        morphExtraH = Math.min(Math.round(vh * 0.42), Math.round(roomBelow));
        // Also widen the sampled area so radar reads as a proper
        // circle rather than a squashed ellipse.
        const desiredW = Math.min(vw * 0.9, morphExtraH + geom.W * 0.5);
        morphExtraW = Math.max(0, desiredW - geom.W);
      } else {
        morphExtraH = 260;
      }
    }
    const canvasH = geom.H + morphExtraH;
    const canvasW = geom.W + morphExtraW;
    // Cache for the animation loop so it can drive the vertical
    // shift on every frame without re-measuring the viewport.
    morphExtraHRef.current = morphExtraH;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    dprRef.current = dpr;
    canvas.width = canvasW * dpr;
    canvas.height = canvasH * dpr;
    canvas.style.width = `${canvasW}px`;
    canvas.style.height = `${canvasH}px`;
    // Centre horizontally beneath the heading — shape samplers use
    // `canvasW` / `canvasH` as their bounds, so the shapes stay
    // centred inside the widened canvas even though the heading dots
    // remain at their original left-anchored positions.
    canvas.style.left = `${geom.left - morphExtraW / 2}px`;
    canvas.style.top = `${geom.top}px`;

    // Shift heading dots to sit inside the widened canvas. Without
    // this, the master dots would land at their original x positions
    // relative to the union, which now sits `morphExtraW/2` to the
    // left of the canvas origin.
    const headingXOffset = morphExtraW / 2;
    geom.dots.forEach((d) => { d.tx += headingXOffset; });

    // Cap the heading dot list — this is the master.
    const master = _capDots(geom.dots);
    // Ask each shape sampler for its points AND its preferred ordering.
    const shapePointLists = (shapeSamplers || []).map((fn) => {
      try {
        const raw = fn({ W: canvasW, H: canvasH });
        // A sampler can return either `[{x,y,c},...]` or
        // `{points:[...], orderBy:"x"|"angle"}`. Both are legal.
        if (Array.isArray(raw)) return { points: raw, orderBy: fn.orderBy || "angle" };
        return { points: raw.points || [], orderBy: raw.orderBy || fn.orderBy || "angle" };
      } catch { return { points: [], orderBy: "angle" }; }
    });
    // One master-key list per ordering strategy so we don't recompute.
    const masterKeyCache = {};
    const _getMasterKeys = (orderBy) => {
      if (!masterKeyCache[orderBy]) masterKeyCache[orderBy] = _computeMasterKeys(master, orderBy);
      return masterKeyCache[orderBy];
    };
    const shapesForDot = shapePointLists.map(({ points, orderBy }) =>
      _mapShapeToMaster(master.length, points, orderBy, _getMasterKeys(orderBy)));

    dotsRef.current = master.map((h, i) => {
      const homes = [
        { x: h.tx, y: h.ty, c: h.c },
        ...shapesForDot.map((arr) => arr[i]),
      ];
      return {
        x: h.tx + (Math.random() - 0.5) * INTRO_JITTER,
        y: h.ty + (Math.random() - 0.5) * INTRO_JITTER,
        vx: 0,
        vy: 0,
        homes,
      };
    });
    if (typeof onReady === "function") {
      onReady({ W: geom.W, H: geom.H, left: geom.left, top: geom.top });
    }
  };

  const _tick = () => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = dprRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.clearRect(0, 0, W, H);

    const dots = dotsRef.current;
    const m = mouseRef.current;
    const p = progressRef.current; // 0..(shapes.length)

    // Shift the canvas up so the shape's centre aligns with the
    // heading's centre (which is the viewport centre in story mode).
    // Ramp: 0 at progress 0, full shift by progress 0.15 so it moves
    // smoothly as the morph begins. `extraH` is the height of the
    // shape area beneath the heading; half of that is exactly the
    // offset needed. Applied via CSS transform so the paint stays
    // GPU-composited and CLS-free.
    const extraH = morphExtraHRef.current || 0;
    const shift = extraH * 0.5 * Math.min(1, p / 0.15);
    canvas.style.transform = shift > 0 ? `translate3d(0, ${-shift}px, 0)` : "translate3d(0, 0, 0)";

    // Interpolate each dot's home between two adjacent stages. `stage`
    // is the integer index of the current segment; `t` is the fraction
    // into that segment. If p >= last, snap to the last home. When
    // there's only one home (no shape samplers wired), we skip the
    // lerp entirely — otherwise `homes[stage+1]` would be undefined.
    const homeCount = dots.length ? dots[0].homes.length : 1;
    const singleHome = homeCount <= 1;
    const raw = p * Math.max(0, homeCount - 1);
    const stage = singleHome ? 0 : Math.max(0, Math.min(homeCount - 2, Math.floor(raw)));
    let t = singleHome ? 0 : raw - stage;
    // Ease t with a smooth-step so the morph feels less linear.
    t = t * t * (3 - 2 * t);

    for (let i = 0; i < dots.length; i++) {
      const d = dots[i];
      const a = d.homes[stage];
      const b = singleHome ? a : d.homes[Math.min(homeCount - 1, stage + 1)];
      if (!a || !b) continue;
      const hx = a.x + (b.x - a.x) * t;
      const hy = a.y + (b.y - a.y) * t;
      // Colour lerp — cheap because our two channels only ever swap
      // between "255,255,255" and "0,240,255" and radar/chart palette.
      // We colour-code by the target stage's home (no per-frame parse).
      const col = t < 0.5 ? a.c : b.c;

      let ax = (hx - d.x) * SPRING_K;
      let ay = (hy - d.y) * SPRING_K;
      if (m.active) {
        const mdx = d.x - m.x;
        const mdy = d.y - m.y;
        const dist2 = mdx * mdx + mdy * mdy;
        if (dist2 < REACT_RADIUS * REACT_RADIUS && dist2 > 0.01) {
          const dist = Math.sqrt(dist2);
          const force = ((REACT_RADIUS - dist) / REACT_RADIUS) * PUSH_MAX;
          ax += (mdx / dist) * force;
          ay += (mdy / dist) * force;
        }
      }
      d.vx = (d.vx + ax) * FRICTION;
      d.vy = (d.vy + ay) * FRICTION;
      d.x += d.vx;
      d.y += d.vy;
      ctx.fillStyle = `rgb(${col})`;
      ctx.fillRect(d.x | 0, d.y | 0, DOT_SIZE, DOT_SIZE);
    }
    rafRef.current = requestAnimationFrame(_tick);
  };

  const _start = () => {
    if (runningRef.current) return;
    runningRef.current = true;
    rafRef.current = requestAnimationFrame(_tick);
  };
  const _stop = () => {
    runningRef.current = false;
    cancelAnimationFrame(rafRef.current);
  };

  useLayoutEffect(() => {
    if (!particlesOn) return;
    let cancelled = false;
    const _init = async () => {
      try { if (document.fonts && document.fonts.ready) await document.fonts.ready; }
      catch { /* older Safari */ }
      if (cancelled) return;
      _resample();
      _start();
      // Round-10 Item 2: the canvas has painted its first frame of
      // dots — only NOW is it safe to fade the solid text out. Pre-R10
      // the fade and the first dot paint were decoupled, which is
      // why a cold load (fonts not cached) briefly showed invisible
      // letters between the particlesOn flip and the first _draw.
      setCanvasReady(true);
    };
    _init();
    let resizeTimer = 0;
    const _onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(_resample, 120);
    };
    window.addEventListener("resize", _onResize, { passive: true });
    const io = new IntersectionObserver((entries) => {
      const inView = entries[0] && entries[0].intersectionRatio > 0.1;
      if (inView && !document.hidden) _start();
      else _stop();
    }, { threshold: [0, 0.1, 0.5] });
    if (wrapRef.current) io.observe(wrapRef.current);
    const _onVis = () => {
      if (document.hidden) _stop();
      else _start();
    };
    document.addEventListener("visibilitychange", _onVis);
    return () => {
      cancelled = true;
      _stop();
      window.removeEventListener("resize", _onResize);
      document.removeEventListener("visibilitychange", _onVis);
      io.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [particlesOn, shapeSamplers]);

  const _onPointerMove = (e) => {
    if (!particlesOn) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    mouseRef.current.x = e.clientX - rect.left;
    mouseRef.current.y = e.clientY - rect.top;
    mouseRef.current.active = true;
  };
  const _onPointerLeave = () => {
    mouseRef.current.active = false;
    mouseRef.current.x = -9999;
    mouseRef.current.y = -9999;
  };

  return (
    <span
      ref={wrapRef}
      className={`kr-particle-heading ${canvasReady ? "kr-particle-on" : ""} ${className}`}
      onMouseMove={particlesOn ? _onPointerMove : undefined}
      onMouseLeave={particlesOn ? _onPointerLeave : undefined}
      style={{ position: "relative", display: "inline-block" }}
    >
      <span ref={whiteRef} className="kr-ph-white">Multi-Market{breakBefore ? "\u00A0" : "\u00A0"}</span>
      {breakBefore && <br className="hidden sm:block" />}
      <span className="kr-title-glow inline-block">
        <span ref={cyanRef} className="kr-title-shimmer kr-ph-cyan">KOL Intelligence.</span>
      </span>
      {particlesOn && (
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="kr-ph-canvas"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
            opacity: canvasOpacity,
            transition: "opacity 260ms linear",
            // Cyan halo without per-frame blur — CSS drop-shadow is
            // GPU-composited but pairing it with `blur()` on a
            // constantly-repainted canvas cost 3-5 % TBT on slower
            // CPUs, so we drop the blur and rely on the drop-shadow
            // pair alone for the "gaps" fill.
            filter: "drop-shadow(0 0 6px rgba(0,240,255,0.45)) drop-shadow(0 0 16px rgba(0,240,255,0.22))",
            animation: "kr-ph-pulse 4.5s ease-in-out infinite",
          }}
        />
      )}
    </span>
  );
}
